import api from '../axios-instance';
import deepMergeObjects from '../../utils/deepMerge';
import { mockRatingHistoryData } from '../__mocks__/ratingHistory.data';

import type { ApiResponse } from './api.types';
import type { PolicyViewModel } from '../types/PolicyViewModel';
import type { MovePolicyResponse } from '../types/MovePolicyResponse';
import type { SearchResultViewModel } from '../types/SearchResultViewModel';
import type { PolicyPeriodViewModel } from '../types/PolicyPeriodViewModel';
import type { PolicyTimelineViewModel } from '../types/PolicyTimelineViewModel';
import type { UnitStatReportViewModel } from '../types/UnitStatReportViewModel';
import type { PolicyExposureViewModel } from '../types/PolicyExposureViewModel';
import type { BatchMovePolicyResponse } from '../types/BatchMovePolicyResponse';
import type { UnitStatSummaryViewModel } from '../types/UnitStatSummaryViewModel';
import type { PolicyComplianceViewModel } from '../types/PolicyComplianceViewModel';
import type { PolicyEndorsementViewModel } from '../types/PolicyEndorsementViewModel';
import type { PolicySearchResultViewModel } from '../types/PolicySearchResultViewModel';
import type { EmployerSearchResultViewModel } from '../types/EmployerSearchResultViewModel';
import type { PolicyPremiumDetailsViewModel } from '../types/PolicyPremiumDetailsViewModel';
import type { UnitStatReportSummaryViewModel } from '../types/UnitStatReportSummaryViewModel';
import type { UnitStatUsrSearchResultViewModel } from '../types/UnitStatUsrSearchResultViewModel';
import type { PolicyNamesAndAddressesViewModel } from '../types/PolicyNamesAndAddressesViewModel';
import type { MovePolicyTargetCoverageViewModel } from '../types/MovePolicyTargetCoverageViewModel';
import type { PolicyTransactionHistoryViewModel } from '../types/PolicyTransactionHistoryViewModel';
import type { UnitStatReportLossSummaryViewModel } from '../types/UnitStatReportLossSummaryViewModel';

function toThrownError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  return new Error('Unexpected non-Error thrown');
}

export type PolicySearchRequest = {
  start?: number;
  end?: number;
  filterModel?: string;
  policyNumber?: string;
  effectiveDate?: string;
  carrierId?: string;
  employerName?: string;
  fein?: string;
  policyStatus?: string;
  governingClass?: string;
  premiumRangeMin?: string;
  premiumRangeMax?: string;
  complianceStatus?: string;
  dateRangeFrom?: string;
  dateRangeTo?: string;
  addressStreet?: string;
  addressCity?: string;
  addressStateCode?: number;
  addressState?: string;
  addressZip?: string;
  pageNumber?: number;
  pageSize?: number;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  groupFields?: string[];
  groupKeys?: string[];
};

export type EmployerSearchRequest = {
  start?: number;
  end?: number;
  filterModel?: string;
  policyNumber?: string;
  coverageId?: number;
  interstateId?: string;
  carrierName?: string;
  carrierId?: string;
  comboId?: number;
  market?: string;
  policyStatus?: string;
  effectiveDate?: string;
  governingClass?: string;
  complianceStatus?: string;
  employerName?: string;
  additionalNames?: boolean;
  fein?: string;
  ratingEffectiveDate?: string;
  ratingStatus?: string;
  dateRangeFrom?: string;
  dateRangeTo?: string;
  addressStreet?: string;
  addressCity?: string;
  addressStateCode?: number;
  addressState?: string;
  addressZip?: string;
  pageNumber?: number;
  pageSize?: number;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  groupFields?: string[];
  groupKeys?: string[];
};

export type UnitStatUsrSearchRequest = {
  start?: number;
  end?: number;
  filterModel?: string;
  policyNumber?: string;
  submissionId?: number;
  carrierSearch?: string;
  effectiveDate?: string;
  reportLevel?: number;
  correctionLevel?: number;
  correctionType?: string;
  lossClaimNumber?: string;
  lossType?: string;
  injuryType?: string;
  claimStatus?: string;
  unitStatus?: string;
  dateRangeFrom?: string;
  dateRangeTo?: string;
  pageNumber?: number;
  pageSize?: number;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  groupFields?: string[];
  groupKeys?: string[];
};

export type StateLookupOption = {
  stateCode: number;
  stateAbbreviation: string;
  stateName: string;
};

export type MarketLookupOption = {
  code: string;
  description: string;
  sortOrder?: number | null;
};

export type PolicyPeriodRatingTypeLookupOption = {
  experienceRatingCode: string;
  experienceRatingDescription: string;
  experienceRatingEnum: string;
};

export type CodeDescriptionLookupOption = {
  code: string;
  description: string;
};

export type ClassCodeLookupOption = {
  classCode: string;
  classDescription: string;
  classEffectiveDate: string;
  classExpirationDate?: string | null;
  statCodeIndicator: boolean;
  subjectToExperienceMod: boolean;
};

export type EndorsementTypeLookupOption = {
  endorsementTypeId: number;
  endorsementCode: string;
  endorsementDescription: string;
  endorsementVersionEffectiveDate?: string | null;
  endorsementVersionExpirationDate?: string | null;
};

export type EndorsementDescriptorTypeLookupOption = {
  descriptorCode: string;
  descriptorDescription: string;
};

export type UpdatePolicyStatePremiumUnifiedRequest = {
  statePremiumId: number;
  stateCode?: number;
  policyChangeEffectiveDate?: string;
  expModFactor?: number;
  estimatedStateStandardPremiumTotal?: number;
};

export type UpdatePolicyExposureRequest = {
  policyExposureId?: number;
  classCode: string;
  exposureActCode?: string;
  manualRate?: number;
  estimatedExposure?: number;
  estimatedPremium?: number;
  policyChangeEffectiveDate?: string;
  exposurePeriodEffectiveDate?: string;
  exposurePeriodExpirationDate?: string;
};

export type UpdatePolicyEndorsementWc000308Request = {
  endorsementWc000308Id?: number;
  descriptorCode?: string;
  excludedName?: string;
};

export type UpdatePolicyEndorsementWc000310Request = {
  endorsementWc000310Id?: number;
  descriptorCode?: string;
  includedName?: string;
};

export type UpdatePolicyEndorsementRequest = {
  endorsementId: number;
  endorsementTypeId: number;
  policyChangeEffectiveDate?: string;
  policyChangeExpirationDate?: string;
  wc000308s?: UpdatePolicyEndorsementWc000308Request[];
  wc000310s?: UpdatePolicyEndorsementWc000310Request[];
};

export type UpdatePolicyAddressRequest = {
  policyAddressId?: number;
  addressId?: number;
  street?: string;
  city?: string;
  stateCode?: number;
  zipcode?: string;
  email?: string;
  effectiveDate?: string;
  expirationDate?: string;
};

export type UpdatePolicyNameRequest = {
  nameId?: number;
  name?: string;
  fein?: string;
  effectiveDate?: string;
  expirationDate?: string;
  email?: string;
  mailingAddress?: UpdatePolicyAddressRequest;
  addresses?: UpdatePolicyAddressRequest[];
};

export type UpdatePolicyNamesAndAddressesRequest = {
  primaryEmployer?: UpdatePolicyNameRequest;
  agency?: UpdatePolicyNameRequest;
  additionalNames?: UpdatePolicyNameRequest[];
  deletedAdditionalNameIds?: number[];
  deletedAdditionalAddressIds?: number[];
};

export type UpdatePolicyPeriodRequest = {
  policyPeriodId: number;
  policyPeriodEffectiveDate?: string;
  experienceRatingCode?: string;
  estimatedStandardPremium?: number;
  statePremiums?: UpdatePolicyStatePremiumUnifiedRequest[];
  exposures?: UpdatePolicyExposureRequest[];
  deletedPolicyExposureIds?: number[];
};

export type UpdatePolicyRequest = {
  policy?: {
    interstateRiskNumber?: string;
    legalNatureCode?: string;
    legalNatureOther?: string;
    retroRatingCode?: string;
    minimumPremiumStateCode?: number;
    planTypeCode?: string;
    producerName?: string;
    policyTypeCode?: string;
    policyEffectiveDate?: string;
    policyExpirationDate?: string;
    policyIssueDate?: string;
    bodilyInjuryAccidentLimit?: number;
    bodilyInjuryDiseasePolicyLimit?: number;
    bodilyInjuryDiseaseEmployeeLimit?: number;
  };
  policyPeriods?: UpdatePolicyPeriodRequest[];
  endorsements?: UpdatePolicyEndorsementRequest[];
  namesAndAddresses?: UpdatePolicyNamesAndAddressesRequest;
  unitStatReports?: UpdatePolicyUnitStatReportRequest[];
  eventDescription?: string;
};

export type UpdatePolicyResponse = {
  policyId: number;
  policyPeriodId: number;
  modifiedDate: string;
  modifiedBy: string;
};

export type UpdatePolicyUnitStatReportRequest = {
  unitStatReportId: number;
  insuredName?: string;
  insuredAddress?: string;
  correctionTypeCode?: string;
  replacementReportIndicator?: boolean;
  exposureStateCode?: number | null;
  stateEffectiveDate?: string | null;
  riskNumber?: string;
  coverageTypeCode?: string;
  planTypeCode?: string;
  estimatedAuditCode?: string;
  lossesSubjectDeductibleCode?: string;
  deductibleClaimAccident?: number | null;
  deductibleAggregate?: number | null;
  basisDeductibleCalculationCode?: string;
  exposures?: UpdatePolicyUnitStatReportExposureRequest[];
  deletedExposureIds?: number[];
};

export type UpdatePolicyUnitStatReportExposureRequest = {
  usrexposureId?: number;
  classCode: string;
  expModFactor?: number;
  expModFactorEffectiveDate?: string;
  rateEffectiveDate?: string;
  exposure?: number;
  manualRate?: number;
  premium?: number;
  splitPeriodCode?: string;
  updateTypeCode?: string;
};

export type UnitStatUsrLookups = {
  correctionTypes: CodeDescriptionLookupOption[];
  updateTypes?: CodeDescriptionLookupOption[];
  coverageTypes: CodeDescriptionLookupOption[];
  marketTypes: CodeDescriptionLookupOption[];
  estimatedAuditTypes: CodeDescriptionLookupOption[];
  lossesSubjectDeductibleTypes: CodeDescriptionLookupOption[];
  basisDeductibleCalculationTypes: CodeDescriptionLookupOption[];
  lossTypes: CodeDescriptionLookupOption[];
  injuryTypes: CodeDescriptionLookupOption[];
  claimStatusTypes: CodeDescriptionLookupOption[];
  unitStatusTypes: CodeDescriptionLookupOption[];
  splitPeriodTypes?: CodeDescriptionLookupOption[];
};

export type PolicySearchStaleRequestStrategy = 'cancel' | 'sequence';

export type PolicySearchExecutionOptions = {
  signal?: AbortSignal;
  staleRequestStrategy?: PolicySearchStaleRequestStrategy;
};

export const policyService = {
  searchPolicies: async (
    params: PolicySearchRequest,
    options?: PolicySearchExecutionOptions
  ): Promise<ApiResponse<SearchResultViewModel<PolicySearchResultViewModel>>> => {
    try {
      const staleRequestStrategy = options?.staleRequestStrategy ?? 'sequence';
      const response = await api.get(
        'search/policies',
        staleRequestStrategy === 'cancel' && options?.signal
          ? { params, signal: options.signal }
          : { params }
      );
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },

  getStateLookups: async (): Promise<ApiResponse<StateLookupOption[]>> => {
    try {
      const response = await api.get('lookups/states');
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },

  getClassCodeLookups: async (): Promise<ApiResponse<ClassCodeLookupOption[]>> => {
    try {
      const response = await api.get('lookups/class-codes');
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },

  getEndorsementTypeLookups: async (): Promise<ApiResponse<EndorsementTypeLookupOption[]>> => {
    try {
      const response = await api.get('lookups/endorsement-types');
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },

  getEndorsementDescriptorTypeLookups: async (): Promise<ApiResponse<EndorsementDescriptorTypeLookupOption[]>> => {
    try {
      const response = await api.get('lookups/endorsement-descriptor-types');
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },

  getMarketLookups: async (): Promise<ApiResponse<MarketLookupOption[]>> => {
    try {
      const response = await api.get('lookups/markets');
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },

  getPolicyPeriodRatingTypeLookups: async (): Promise<ApiResponse<PolicyPeriodRatingTypeLookupOption[]>> => {
    try {
      const response = await api.get('lookups/policy-period-rating-types');
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },

  getPolicyPeriodEditExperienceRatingTypeLookups: async (): Promise<ApiResponse<PolicyPeriodRatingTypeLookupOption[]>> => {
    try {
      const response = await api.get('lookups/policy-period-edit-experience-rating-types');
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },

  getUnitStatUsrLookups: async (): Promise<ApiResponse<UnitStatUsrLookups>> => {
    try {
      const response = await api.get('lookups/unit-stat-usr-search');
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },

  getPolicyLegalNatureLookups: async (): Promise<ApiResponse<CodeDescriptionLookupOption[]>> => {
    try {
      const response = await api.get('lookups/policy-legal-natures');
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },

  getPolicyTypeLookups: async (): Promise<ApiResponse<CodeDescriptionLookupOption[]>> => {
    try {
      const response = await api.get('lookups/policy-types');
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },

  getPolicyPlanTypeLookups: async (): Promise<ApiResponse<CodeDescriptionLookupOption[]>> => {
    try {
      const response = await api.get('lookups/policy-plan-types');
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },

  getPolicyRetroRatingLookups: async (): Promise<ApiResponse<CodeDescriptionLookupOption[]>> => {
    try {
      const response = await api.get('lookups/policy-retro-ratings');
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },

  searchEmployers: async (
    params: EmployerSearchRequest,
    options?: PolicySearchExecutionOptions
  ): Promise<ApiResponse<SearchResultViewModel<EmployerSearchResultViewModel>>> => {
    try {
      const staleRequestStrategy = options?.staleRequestStrategy ?? 'sequence';
      const response = await api.get(
        'search/employers',
        staleRequestStrategy === 'cancel' && options?.signal
          ? { params, signal: options.signal }
          : { params }
      );
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },

  searchUnitStatUsr: async (
    params: UnitStatUsrSearchRequest,
    options?: PolicySearchExecutionOptions
  ): Promise<ApiResponse<SearchResultViewModel<UnitStatUsrSearchResultViewModel>>> => {
    try {
      const staleRequestStrategy = options?.staleRequestStrategy ?? 'sequence';
      const response = await api.get(
        'search/unit-stat-usr',
        staleRequestStrategy === 'cancel' && options?.signal
          ? { params, signal: options.signal }
          : { params }
      );
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },

  getPolicyById: async (
    comboId: number,
    coverageId: number,
    policyId: number
  ): Promise<ApiResponse<PolicyViewModel>> => {
    try {
      const response = await api.get(
        `combos/${comboId}/coverages/${coverageId}/policies/${policyId}`
      );
      // Handle array merging properly
      const responseData = response.data;
      const mergedData: PolicyViewModel = deepMergeObjects(responseData, mockRatingHistoryData);

      return {
        data: mergedData,
        status: response.status,
      };
    } catch (error) {
      throw toThrownError(error);
    }
  },

  getEndorsementsByPolicyId: async (
    comboId: number,
    coverageId: number,
    policyId: number
  ): Promise<ApiResponse<PolicyEndorsementViewModel[]>> => {
    try {
      const response = await api.get(
        `combos/${comboId}/coverages/${coverageId}/policies/${policyId}/endorsements`
      );
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },

  getPremiumsByPolicyId: async (
    comboId: number,
    coverageId: number,
    policyId: number
  ): Promise<ApiResponse<PolicyPremiumDetailsViewModel[]>> => {
    try {
      const response = await api.get(
        `combos/${comboId}/coverages/${coverageId}/policies/${policyId}/premium-details`
      );
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },

  getComplianceHistoryByPolicyId: async (
    comboId: number,
    coverageId: number,
    policyId: number
  ): Promise<ApiResponse<PolicyComplianceViewModel[]>> => {
    try {
      const response = await api.get(
        `combos/${comboId}/coverages/${coverageId}/policies/${policyId}/compliance-history`
      );
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },

  getUnitStatReportVersionsByPolicyId: async (
    comboId: number,
    coverageId: number,
    policyId: number
  ): Promise<ApiResponse<UnitStatReportSummaryViewModel[]>> => {
    try {
      const response = await api.get(
        `combos/${comboId}/coverages/${coverageId}/policies/${policyId}/unit-stat-report-versions`
      );
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },

  getUnitStatReportByPolicyIdAndReportNumber: async (
    comboId: number,
    coverageId: number,
    policyId: number | undefined,
    reportNumber: number | undefined,
    correctionSequenceNumber: number | undefined
  ): Promise<ApiResponse<UnitStatReportViewModel>> => {
    try {
      const response = await api.get(
        `combos/${comboId}/coverages/${coverageId}/policies/${policyId}/unit-stat-reports`,
        {
          params: {
            reportNumber,
            correctionSequenceNumber,
          },
        }
      );
      return response;
    } catch (error) {
      throw toThrownError(error);
    }
  },

  getPeriodsByPolicyId: async (
    comboId: number,
    coverageId: number,
    policyId: number
  ): Promise<ApiResponse<PolicyPeriodViewModel[]>> => {
    try {
      const response = await api.get(
        `combos/${comboId}/coverages/${coverageId}/policies/${policyId}/periods`
      );
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },
  getExposuresByPolicyId: async (
    comboId: number,
    coverageId: number,
    policyId: number
  ): Promise<ApiResponse<PolicyExposureViewModel[]>> => {
    try {
      const response = await api.get(
        `combos/${comboId}/coverages/${coverageId}/policies/${policyId}/exposures`
      );
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },
  getPolicyTimelineByPolicyId: async (
    comboId: number,
    coverageId: number,
    policyId: number
  ): Promise<ApiResponse<PolicyTimelineViewModel>> => {
    try {
      const response = await api.get(
        `combos/${comboId}/coverages/${coverageId}/policies/${policyId}/timeline`
      );
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },
  getPolicyNamesAndAddresses: async (
    comboId: number,
    coverageId: number,
    policyId: number
  ): Promise<ApiResponse<PolicyNamesAndAddressesViewModel>> => {
    try {
      const response = await api.get(
        `combos/${comboId}/coverages/${coverageId}/policies/${policyId}/names-and-addresses`
      );
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },
  getUnitStatLossesSummaryByPolicyId: async (
    comboId: number,
    coverageId: number,
    policyId: number
  ): Promise<ApiResponse<UnitStatReportLossSummaryViewModel[]>> => {
    try {
      const response = await api.get(
        `combos/${comboId}/coverages/${coverageId}/policies/${policyId}/unit-stat-losses-summary`
      );
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },
  getUnitStatSummaryByPolicyId: async (
    comboId: number,
    coverageId: number,
    policyId: number
  ): Promise<ApiResponse<UnitStatSummaryViewModel[]>> => {
    try {
      const response = await api.get(
        `combos/${comboId}/coverages/${coverageId}/policies/${policyId}/unit-stat-summary`
      );
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },
  getTransactionHistoryByPolicyId: async (
    comboId: number,
    coverageId: number,
    policyId: number
  ): Promise<ApiResponse<PolicyTransactionHistoryViewModel[]>> => {
    try {
      const response = await api.get(
        `combos/${comboId}/coverages/${coverageId}/policies/${policyId}/transaction-history`
      );
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },

  updatePolicy: async (
    comboId: number,
    coverageId: number,
    policyId: number,
    request: UpdatePolicyRequest
  ): Promise<ApiResponse<UpdatePolicyResponse>> => {
    try {
      const response = await api.patch(
        `combos/${comboId}/coverages/${coverageId}/policies/${policyId}`,
        request
      );
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },

  movePolicyToExistingCombo: async (params: {
    comboId: number;
    coverageId: number;
    policyId: number;
    targetComboId: number;
    note: string;
  }): Promise<ApiResponse<MovePolicyResponse>> => {
    try {
      const response = await api.post(
        `combos/${params.comboId}/coverages/${params.coverageId}/policies/${params.policyId}/move`,
        {
          targetComboId: params.targetComboId,
          note: params.note,
        }
      );
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },

  getMoveTargetCoverage: async (params: {
    comboId: number;
    coverageId: number;
    policyId: number;
    targetCoverageId: number;
  }): Promise<ApiResponse<MovePolicyTargetCoverageViewModel>> => {
    try {
      const response = await api.get(
        `combos/${params.comboId}/coverages/${params.coverageId}/policies/${params.policyId}/target-coverages/${params.targetCoverageId}`
      );
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },

  movePolicyToExistingCoverage: async (params: {
    comboId: number;
    coverageId: number;
    policyId: number;
    targetCoverageId: number;
    note: string;
  }): Promise<ApiResponse<MovePolicyResponse>> => {
    try {
      const response = await api.post(
        `combos/${params.comboId}/coverages/${params.coverageId}/policies/${params.policyId}/move-existing-coverage`,
        {
          targetCoverageId: params.targetCoverageId,
          note: params.note,
        }
      );
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },

  movePolicyToNewCombo: async (params: {
    comboId: number;
    coverageId: number;
    policyId: number;
    note: string;
  }): Promise<ApiResponse<MovePolicyResponse>> => {
    try {
      const response = await api.post(
        `combos/${params.comboId}/coverages/${params.coverageId}/policies/${params.policyId}/move-new-combo`,
        {
          note: params.note,
        }
      );
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },

  movePoliciesBatchToExistingCoverage: async (params: {
    comboId: number;
    coverageId: number;
    policyIds: number[];
    targetCoverageId: number;
    note: string;
  }): Promise<ApiResponse<BatchMovePolicyResponse>> => {
    try {
      const response = await api.post(
        `combos/${params.comboId}/coverages/${params.coverageId}/policies/batch-move-existing-coverage`,
        {
          policyIds: params.policyIds,
          targetCoverageId: params.targetCoverageId,
          note: params.note,
        }
      );
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },

  movePoliciesBatchToExistingCombo: async (params: {
    comboId: number;
    coverageId: number;
    policyIds: number[];
    targetComboId: number;
    note: string;
  }): Promise<ApiResponse<BatchMovePolicyResponse>> => {
    try {
      const response = await api.post(
        `combos/${params.comboId}/coverages/${params.coverageId}/policies/batch-move`,
        {
          policyIds: params.policyIds,
          targetComboId: params.targetComboId,
          note: params.note,
        }
      );
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },

  movePoliciesBatchToNewCombo: async (params: {
    comboId: number;
    coverageId: number;
    policyIds: number[];
    note: string;
  }): Promise<ApiResponse<BatchMovePolicyResponse>> => {
    try {
      const response = await api.post(
        `combos/${params.comboId}/coverages/${params.coverageId}/policies/batch-move-new-combo`,
        {
          policyIds: params.policyIds,
          note: params.note,
        }
      );
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },
};
